import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { db, storage, auth } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import AuthShell from "../components/AuthShell";
import { showToast } from "../utils/toast";
import { getAuthErrorMessage } from "../utils/authErrors";

const fieldClassName =
  "w-full rounded-2xl border border-[#ead7b0] bg-white/90 px-4 py-3 text-[#4e3520] shadow-[0_12px_30px_-28px_rgba(69,39,15,0.45)] outline-none transition placeholder:text-[#b39368] focus:border-[#c99649] focus:ring-2 focus:ring-[#efd8aa]";

const uploadFieldClassName =
  "block w-full rounded-2xl border border-[#ead7b0] bg-white/90 px-4 py-3 text-sm text-[#6c4d2c] shadow-[0_12px_30px_-28px_rgba(69,39,15,0.45)] file:mr-4 file:rounded-xl file:border-0 file:bg-[#fff1d6] file:px-4 file:py-2 file:font-semibold file:text-[#8a5a1d] hover:file:bg-[#f9e4b5]";

const primaryButtonClassName =
  "w-full rounded-2xl bg-[linear-gradient(135deg,#a56a22,#d6a654)] px-4 py-3 text-sm font-semibold text-white shadow-[0_20px_40px_-24px_rgba(118,73,20,0.65)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60";

const InstructorRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    whatsapp: "",
    nic: "",
    password: "",
    confirmPassword: "",
    bankName: "",
    bankAccount: "",
    bankBranch: "",
    accountHolder: "",
    profilePic: null,
    nicFrontPic: null,
    nicBackPic: null,
    bankFrontPic: null,
  });

  const [preview, setPreview] = useState(null);
  const [nicFrontPreview, setNicFrontPreview] = useState(null);
  const [nicBackPreview, setNicBackPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [referralParam, setReferralParam] = useState("");
  const [referrer, setReferrer] = useState(null);
  const [referralNotice, setReferralNotice] = useState("");
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralResolved, setReferralResolved] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get("ref")?.trim() || "";
    setReferralParam(refCode);
    setReferralResolved(false);

    if (!refCode) {
      setReferralNotice("");
      setReferrer(null);
      setReferralLoading(false);
      setReferralResolved(true);
      return;
    }

    (async () => {
      setReferralLoading(true);
      try {
        let found = null;

        // 1. Check referral_links by ID
        try {
          const snap = await getDoc(doc(db, "referral_links", refCode));
          if (snap.exists()) {
            const d = snap.data() || {};
            found = {
              id: d.ownerUserId || d.userId || d.uid || snap.id,
              referralCode: d.referralCode || refCode,
              fullName: d.fullName || "",
              email: d.email || "",
              level: d.level != null ? Number(d.level) : 1,
              status: d.status || "active",
            };
          }
        } catch (e) {
          console.warn("referral_links by id error", e);
        }

        // 2. Check referral_links by referralCode query
        if (!found) {
          try {
            const qSnap = await getDocs(
              query(collection(db, "referral_links"), where("referralCode", "==", refCode), limit(1))
            );
            if (!qSnap.empty) {
              const d = qSnap.docs[0].data() || {};
              found = {
                id: d.ownerUserId || d.userId || d.uid || qSnap.docs[0].id,
                referralCode: d.referralCode || refCode,
                fullName: d.fullName || "",
                email: d.email || "",
                level: d.level != null ? Number(d.level) : 1,
                status: d.status || "active",
              };
            }
          } catch (e) {
            console.warn("referral_links by query error", e);
          }
        }

        // 3. Check referral_users by referralCode query
        if (!found) {
          try {
            const ruSnap = await getDocs(
              query(collection(db, "referral_users"), where("referralCode", "==", refCode), limit(1))
            );
            if (!ruSnap.empty) {
              const d = ruSnap.docs[0].data() || {};
              found = {
                id: d.uid || d.userId || ruSnap.docs[0].id,
                referralCode: d.referralCode || refCode,
                fullName: d.fullName || "",
                email: d.email || "",
                level: d.level != null ? Number(d.level) : 1,
                status: d.status || "approved",
              };
            }
          } catch (e) {
            console.warn("referral_users by referralCode error", e);
          }
        }

        // 4. Check referral_users by doc id
        if (!found) {
          try {
            const ruDocSnap = await getDoc(doc(db, "referral_users", refCode));
            if (ruDocSnap.exists()) {
              const d = ruDocSnap.data() || {};
              found = {
                id: d.uid || d.userId || ruDocSnap.id,
                referralCode: d.referralCode || refCode,
                fullName: d.fullName || "",
                email: d.email || "",
                level: d.level != null ? Number(d.level) : 1,
                status: d.status || "approved",
              };
            }
          } catch (e) {
            console.warn("referral_users by doc id error", e);
          }
        }

        // 5. Check referral_codes by doc id
        if (!found) {
          try {
            const rcSnap = await getDoc(doc(db, "referral_codes", refCode));
            if (rcSnap.exists()) {
              const d = rcSnap.data() || {};
              found = {
                id: d.ownerUserId || d.userId || d.uid || refCode,
                referralCode: refCode,
                fullName: d.fullName || "Affiliate Sponsor",
                email: d.email || "",
                level: d.level != null ? Number(d.level) : 1,
                status: d.enabled ? "active" : "inactive",
              };
            }
          } catch (e) {
            console.warn("referral_codes by id error", e);
          }
        }

        if (!found) {
          setReferrer(null);
          setReferralNotice("Invalid referral link. Proceeding without referral.");
          setReferralResolved(true);
          return;
        }

        const isApproved = found.status === "active" || found.status === "approved" || found.status === "enabled";
        if (!isApproved) {
          setReferrer(null);
          setReferralNotice("Referrer is not approved yet. Proceeding without referral.");
          setReferralResolved(true);
          return;
        }

        if (found.level != null && Number(found.level) >= 3) {
          setReferrer(null);
          setReferralNotice("This referrer is Level 3 and cannot invite instructors. Proceeding without referral.");
          setReferralResolved(true);
          return;
        }

        setReferrer(found);
        setReferralNotice(`Referral applied from ${found.fullName || found.email || "affiliate"}.`);
        setReferralResolved(true);
      } catch (err) {
        setReferrer(null);
        setReferralNotice("Could not verify referral. Proceeding without referral.");
        setReferralResolved(true);
      } finally {
        setReferralLoading(false);
      }
    })();
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profilePic") {
      setForm({ ...form, profilePic: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else if (name === "nicFrontPic") {
      setForm({ ...form, nicFrontPic: files[0] });
      setNicFrontPreview(URL.createObjectURL(files[0]));
    } else if (name === "nicBackPic") {
      setForm({ ...form, nicBackPic: files[0] });
      setNicBackPreview(URL.createObjectURL(files[0]));
    } else if (name === "bankFrontPic") {
      setForm({ ...form, bankFrontPic: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match!");
      return;
    }
    if (referralParam && !referralResolved) {
      setError("Checking the referral link. Please wait a moment and try again.");
      return;
    }
    setLoading(true);

    try {
      const userCred = await createUserWithEmailAndPassword(
        auth,
        form.email.trim(),
        form.password
      );
      const uid = userCred.user.uid;

      let profilePicUrl = "";
      let nicFrontUrl = "";
      let nicBackUrl = "";
      let bankFrontUrl = "";

      if (form.profilePic) {
        const profileRef = ref(storage, `profile_images/${uid}`);
        await uploadBytes(profileRef, form.profilePic);
        profilePicUrl = await getDownloadURL(profileRef);
      }
      if (form.nicFrontPic) {
        const nicFrontRef = ref(storage, `nic_front_images/${uid}`);
        await uploadBytes(nicFrontRef, form.nicFrontPic);
        nicFrontUrl = await getDownloadURL(nicFrontRef);
      }
      if (form.nicBackPic) {
        const nicBackRef = ref(storage, `nic_back_images/${uid}`);
        await uploadBytes(nicBackRef, form.nicBackPic);
        nicBackUrl = await getDownloadURL(nicBackRef);
      }
      if (form.bankFrontPic) {
        const bankFrontRef = ref(storage, `bank_images/${uid}`);
        await uploadBytes(bankFrontRef, form.bankFrontPic);
        bankFrontUrl = await getDownloadURL(bankFrontRef);
      }

      const referralPayload = referrer
        ? {
            referredByReferralCode: referrer.referralCode || "",
            referredByUid: referrer.id || "",
            referredByLevel: referrer.level ?? null,
            referredByStatus: referrer.status || "",
            referralKind: "instructor",
            sponsorUid: referrer.id || "",
            referralCode: referrer.referralCode || "",
            referredAt: serverTimestamp(),
          }
        : {
            referredByReferralCode: "",
            referredByUid: "",
            referredByLevel: null,
            referredByStatus: "",
            referralKind: "instructor",
            sponsorUid: "",
            referralCode: "",
            referredAt: serverTimestamp(),
          };

      await setDoc(doc(db, "users", uid), {
        uid,
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        whatsapp: form.whatsapp,
        nic: form.nic,
        bankName: form.bankName,
        bankAccount: form.bankAccount,
        bankBranch: form.bankBranch,
        accountHolder: form.accountHolder,
        profilePicUrl,
        nicFrontUrl,
        nicBackUrl,
        bankFrontUrl,
        role: "instructor",
        status: "pending",
        createdAt: serverTimestamp(),
        ...referralPayload,
      });

      // Synchronize to referral_users so network hierarchy can find instructor
      await setDoc(
        doc(db, "referral_users", uid),
        {
          uid,
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
          whatsapp: form.whatsapp,
          role: "instructor",
          sponsorUid: referrer ? referrer.id : "",
          referredByUid: referrer ? referrer.id : "",
          referredBy: referrer ? referrer.referralCode : "",
          referralCode: referrer ? referrer.referralCode : "",
          referralKind: "instructor",
          status: "pending",
          createdAt: serverTimestamp(),
        },
        { merge: true }
      ).catch((e) => console.warn("referral_users sync skipped", e));

      // Synchronize to stores/{uid} so public marketplace storefront attributes affiliate
      await setDoc(
        doc(db, "stores", uid),
        {
          ownerUserId: uid,
          sponsorUid: referrer ? referrer.id : null,
          affiliateReferrerUid: referrer ? referrer.id : null,
          referrerUserId: referrer ? referrer.id : null,
          referralCode: referrer ? referrer.referralCode : null,
          referredByReferralCode: referrer ? referrer.referralCode : null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      ).catch((e) => console.warn("stores sync skipped", e));

      setLoading(false);
      showToast("Registration successful! Welcome to the shop owner portal.", {
        type: "success",
        duration: 3600,
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(getAuthErrorMessage(err, "register"));
      setLoading(false);
    }
  };

  return (
    <AuthShell
      portalLabel="Shop Owner Portal"
      badge="Create Your Account"
      title="Join as a shop owner"
      description="Create your shop-owner account with the same premium Ceylon Web Mall auth layout while keeping your approval and referral flow intact."
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-[#9b6b2f] transition hover:text-[#7e541f]"
          >
            Login
          </Link>
        </>
      }
    >
      <div className="mx-auto w-full max-w-lg">
        {referralParam && (
          <div
            className={`mb-4 rounded-2xl border px-4 py-3 text-center text-sm font-semibold ${
              referrer
                ? "border-[#c8ddb3] bg-[#f4faee] text-[#527239]"
                : "border-[#ead7b0] bg-[#fff9ef] text-[#8a5a1d]"
            }`}
          >
            {referralLoading ? "Checking referral link..." : referralNotice}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-2xl border border-[#e7b0ab] bg-[#fff4f3] px-4 py-3 text-sm text-[#9f3b2f]">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
              Full Name
            </label>
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              className={fieldClassName}
              value={form.fullName}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
              Email
            </label>
            <input
              type="email"
              name="email"
              placeholder="you@example.com"
              className={fieldClassName}
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                Phone
              </label>
              <input
                type="text"
                name="phone"
                placeholder="Phone"
                className={fieldClassName}
                value={form.phone}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                WhatsApp
              </label>
              <input
                type="text"
                name="whatsapp"
                placeholder="WhatsApp"
                className={fieldClassName}
                value={form.whatsapp}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                NIC (Optional)
              </label>
              <input
                type="text"
                name="nic"
                placeholder="NIC"
                className={fieldClassName}
                value={form.nic}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                Password
              </label>
              <input
                type="password"
                name="password"
                placeholder="********"
                className={fieldClassName}
                value={form.password}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                placeholder="********"
                className={fieldClassName}
                value={form.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                Profile Picture (Optional)
              </label>
              <input
                type="file"
                name="profilePic"
                accept="image/*"
                onChange={handleChange}
                className={uploadFieldClassName}
              />
              {preview && (
                <img
                  src={preview}
                  alt="Profile Preview"
                  className="mt-3 h-16 w-16 rounded-2xl border border-[#ead7b0] object-cover"
                />
              )}
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                NIC Front (Optional)
              </label>
              <input
                type="file"
                name="nicFrontPic"
                accept="image/*"
                onChange={handleChange}
                className={uploadFieldClassName}
              />
              {nicFrontPreview && (
                <img
                  src={nicFrontPreview}
                  alt="NIC Front Preview"
                  className="mt-3 h-16 w-24 rounded-2xl border border-[#ead7b0] object-cover"
                />
              )}
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                NIC Back (Optional)
              </label>
              <input
                type="file"
                name="nicBackPic"
                accept="image/*"
                onChange={handleChange}
                className={uploadFieldClassName}
              />
              {nicBackPreview && (
                <img
                  src={nicBackPreview}
                  alt="NIC Back Preview"
                  className="mt-3 h-16 w-24 rounded-2xl border border-[#ead7b0] object-cover"
                />
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-[#f0dfc0] bg-[#fff9ef] px-4 py-3 text-sm leading-6 text-[#7d5a2a]">
            NIC details and identity images are optional at registration. You
            can complete them later from your profile page.
          </div>

            <button
              type="submit"
              disabled={loading || (referralParam && !referralResolved)}
              className={primaryButtonClassName}
            >
              {loading
                ? "Registering..."
                : referralParam && !referralResolved
                  ? "Checking referral..."
                  : "Register"}
            </button>
        </form>
      </div>
    </AuthShell>
  );
};

export default InstructorRegister;
